
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

export default function About() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "mainEntity": {
      "@type": "Person",
      "name": "Steven Eric Javidi",
      "jobTitle": "Houston Storyteller & Guide Writer",
      "description": "For over a decade, Steven Eric Javidi has been documenting Houston's untold stories, weaving together the threads of history, culture, and natural beauty that make this city extraordinary.",
      "knowsAbout": [
        "Houston History",
        "Houston Heights",
        "Urban Planning",
        "Historical Research",
        "Cultural Heritage",
        "Houston Neighborhoods"
      ],
      "areaServed": {
        "@type": "City",
        "name": "Houston",
        "addressRegion": "Texas",
        "addressCountry": "US"
      }
    }
  };

  return (
    <div className="min-h-screen bg-neutral-100">
      <SEO
        title="About Steven Eric Javidi - Houston Storyteller & Guide Writer"
        description="Learn about Steven Eric Javidi, Houston-based storyteller who explores where the city's history meets its natural landscapes. Over a decade of documenting Houston's untold stories."
        keywords="Steven Eric Javidi, Houston storyteller, Houston historian, Houston guide writer, Houston Heights, urban exploration"
        ogTitle="About Steven Eric Javidi - Houston Storyteller & Guide Writer"
        ogDescription="Learn about Steven Eric Javidi, Houston-based storyteller who explores where the city's history meets its natural landscapes."
        ogType="profile"
        ogUrl={`${import.meta.env.VITE_SITE_URL}/about`}
        twitterTitle="About Steven Eric Javidi - Houston Storyteller & Guide Writer"
        twitterDescription="Learn about Steven Eric Javidi, Houston-based storyteller who explores where the city's history meets its natural landscapes."
        jsonLd={jsonLd}
      />

      {/* Navigation */}
      <nav className="bg-neutral-800 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="text-xl font-semibold text-white">
                Eric Javidi
              </Link>
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                <Link to="/" className="text-neutral-300 hover:text-green-400 px-3 py-2 text-sm font-medium">
                  Home
                </Link>
                <Link to="/about" className="text-white hover:text-green-400 px-3 py-2 text-sm font-medium">
                  About
                </Link>
                <Link to="/guides" className="text-neutral-300 hover:text-green-400 px-3 py-2 text-sm font-medium">
                  Guides
                </Link>
                <Link to="/resources" className="text-neutral-300 hover:text-green-400 px-3 py-2 text-sm font-medium">
                  Resources
                </Link>
                <Link to="/contact" className="text-neutral-300 hover:text-green-400 px-3 py-2 text-sm font-medium">
                  Contact
                </Link>
              </div>
            </div>
            <div className="md:hidden">
              <button className="text-neutral-300 hover:text-white">
                <i className="ri-menu-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Steven's Photo Section - MOVED BELOW NAVIGATION */}
      <section className="bg-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <div className="text-center">
              <div className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden shadow-lg">
                <img
                  src="https://static.readdy.ai/image/712961abb04a260210181117e87ae8f7/a29ff81edfcd47fedcda8a760c61ba7b.png"
                  alt="Eric Javidi"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <h1 className="text-2xl font-bold text-neutral-800">Steven Eric Javidi</h1>
              <p className="text-neutral-600">Houston Guide Creator</p>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 bg-neutral-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="bg-white p-12 rounded-lg shadow-lg max-w-4xl">
                <h1 className="text-5xl lg:text-6xl font-bold text-neutral-800 mb-8">
                  About Eric Javidi
                </h1>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <p className="text-xl text-neutral-600 mb-6">
                      For over a decade, Eric has been documenting Houston's untold stories, weaving together the threads of history, culture, and natural beauty that make this city extraordinary.
                    </p>
                    <p className="text-xl text-neutral-600">
                      Eric's approach combines the rigor of a historian with the curiosity of an explorer. He spends countless hours in city archives, interviewing longtime residents, and walking every trail he recommends.
                    </p>
                  </div>
                  <div>
                    <p className="text-xl text-neutral-600">
                      Based in Houston's Heights neighborhood, Eric has witnessed firsthand how the city continues to evolve while honoring its roots. His mission is simple: to help others fall in love with Houston the way he has, one thoughtful exploration at a time.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="w-full h-96 bg-cover bg-center rounded-lg shadow-lg">
                <img
                  src="https://static.readdy.ai/image/712961abb04a260210181117e87ae8f7/9daeab8ab2163d3cb6031416cc855f0d.png"
                  alt="Eric Javidi"
                  className="w-full h-full object-cover object-center rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-16 lg:py-24 bg-neutral-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-neutral-800 mb-12 text-center">
            My Approach
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-neutral-50 p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-book-open-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-neutral-800 mb-4">
                Research-Based
              </h3>
              <p className="text-neutral-600">
                Draws on archives, local experts, and city records to provide accurate historical context and cultural insights.
              </p>
            </div>
            
            <div className="bg-neutral-50 p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-footprint-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-neutral-800 mb-4">
                Field-Tested
              </h3>
              <p className="text-neutral-600">
                Field-tested routes with detailed logistics, including timing, parking, and accessibility considerations.
              </p>
            </div>
            
            <div className="bg-neutral-50 p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-parent-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-neutral-800 mb-4">
                Family-Focused
              </h3>
              <p className="text-neutral-600">
                Notes on accessibility and family suitability, ensuring activities work for various ages and abilities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 lg:py-24 bg-neutral-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-12">
            What Drives This Work
          </h2>
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-heart-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold mb-4">
                Love for Houston
              </h3>
              <p className="text-neutral-300">
                A deep appreciation for Houston's unique blend of cultures, landscapes, and stories that make it unlike any other city.
              </p>
            </div>
            
            <div>
              <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-compass-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold mb-4">
                Accessible Discovery
              </h3>
              <p className="text-neutral-300">
                Making Houston's rich history and natural beauty accessible to everyone, regardless of their familiarity with the city.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-green-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-neutral-800 mb-6">
            Ready to Explore?
          </h2>
          <p className="text-lg text-neutral-600 mb-8">
            Browse the guides to start discovering Houston's hidden stories and natural spaces.
          </p>
          <Link
            to="/guides"
            className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 whitespace-nowrap cursor-pointer"
          >
            Browse All Guides
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <h3 className="text-xl font-semibold mb-4">Eric Javidi</h3>
              <p className="text-neutral-300 mb-4">
                Houston-based storyteller exploring where the city's history meets its natural landscapes.
              </p>
              <div className="flex items-center space-x-4">
                <a
                  href="https://www.youtube.com/@EricJavidi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-neutral-300 hover:text-red-400 transition-colors"
                  aria-label="YouTube Channel"
                >
                  <i className="ri-youtube-fill text-2xl"></i>
                </a>
                <a
                  href="https://www.facebook.com/ericjavidihouston/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-neutral-300 hover:text-blue-400 transition-colors"
                  aria-label="Facebook Page"
                >
                  <i className="ri-facebook-fill text-2xl"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-neutral-300 hover:text-white">About</Link></li>
                <li><Link to="/guides" className="text-neutral-300 hover:text-white">Guides</Link></li>
                <li><Link to="/resources" className="text-neutral-300 hover:text-white">Resources</Link></li>
                <li><Link to="/contact" className="text-neutral-300 hover:text-white">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Connect</h4>
              <ul className="space-y-2">
                <li><a href="https://www.youtube.com/@EricJavidi" target="_blank" rel="noopener noreferrer" className="text-neutral-300 hover:text-white">YouTube Channel</a></li>
                <li><a href="https://www.facebook.com/ericjavidihouston/" target="_blank" rel="noopener noreferrer" className="text-neutral-300 hover:text-white">Facebook Page</a></li>
                <li><a href="https://readdy.ai/?origin=logo" className="text-neutral-300 hover:text-white">Made with Readdy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-neutral-700 mt-8 pt-8 text-center">
            <p className="text-neutral-400">
              © 2024 Steven Eric Javidi. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
